import { useState } from 'react';
import { useFormik } from 'formik';
import { Box, Grid } from '@mui/material';
import { Button, Dialog, FormikInputPhone, FormikInputString, H1, Notification, P1R, P2 } from 'src/components';
import { useTranslation } from 'react-i18next';

import { CommonApiError } from 'src/api/api.types';
import { themeColors } from 'src/theme';
import { inviteAdminSchema } from 'src/utils/validationSchemes';
import { InviteAdminValues, useInviteTenantSuperAdmin } from 'src/api/tenantSuperAdmin';

type InviteAdminDialogProps = {
  isCompanyList?: boolean;
  setEmail?: (email: string) => void;
  onClose: () => void;
  currentTenantId: number;
};

const InviteAdminDialog = ({ isCompanyList = false, setEmail, onClose, currentTenantId }: InviteAdminDialogProps) => {
  const { t } = useTranslation();
  const [inviteError, setInviteError] = useState<string | null>(null);

  const initialValues: InviteAdminValues = {
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
  };

  const formik = useFormik({
    initialValues,
    validationSchema: inviteAdminSchema,
    onSubmit: async (data) => {
      mutateInviteAdmin({
        id: currentTenantId,
        body: data,
      });
    },
  });

  const { mutate: mutateInviteAdmin, isPending: isInvitingAdmin } = useInviteTenantSuperAdmin({
    meta: {
      setError: formik.setErrors,
    },
    onSuccess: () => {
      setEmail && setEmail(formik.values.email);
      handleClose();
      formik.resetForm();
    },
    onError: (dataError: CommonApiError) => {
      if (dataError.data) {
        const {
          data: { error },
        } = dataError;

        if (error.messages && typeof error.messages === 'string') {
          setInviteError(error.messages);
        }
      }
    },
  });

  const onCloseNotification = () => {
    setInviteError(null);
    // dispatch(inviteTenantAdmin.reset());
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
    onCloseNotification();
  };

  return (
    <>
      <Dialog open aria-labelledby="customized-dialog-title" onClose={handleClose}>
        <Box sx={{ gap: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <H1>{t('companyPage.inviteAdminTitle')}</H1>
          <P1R sx={{ lineHeight: '18.2px', textAlign: 'center', whiteSpace: 'pre-line' }}>{t('companyPage.inviteAdminSubtitle')}</P1R>
        </Box>

        <Grid container sx={{ margin: '32px 0 16px 0', display: 'block' }}>
          <Grid item xs={12} sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%' }}>
              <FormikInputString formik={formik} field="email" label={t('companyPage.emailAdress')} />
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%' }}>
              <FormikInputPhone formik={formik} field="phone" label={t('companyPage.phone')} />
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%' }}>
              <FormikInputString formik={formik} field="firstName" label={t('companyPage.firstName')} />
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%' }}>
              <FormikInputString formik={formik} field="lastName" label={t('companyPage.lastName')} />
            </Box>
          </Grid>
        </Grid>

        <Grid container columnSpacing={2} sx={{ mb: 5 }}>
          <Grid item xs={6}>
            <Button primary={false} onClick={onClose} sx={{ width: '100%', textTransform: 'capitalize' }}>
              {isCompanyList ? t('companyPage.doItLater') : t('common.cancel')}
            </Button>
          </Grid>

          <Grid item xs={6}>
            <Button
              onClick={() => formik.handleSubmit()}
              loading={isInvitingAdmin}
              sx={{
                width: '100%',
                textTransform: 'capitalize',
              }}
            >
              {isCompanyList ? t('common.save') : t('companyPage.sendInvite')}
            </Button>
          </Grid>
        </Grid>
      </Dialog>

      <Notification severity="error" open={Boolean(inviteError)} onClose={onCloseNotification}>
        <P2 sx={{ color: themeColors.white, textTransform: 'none' }}>{inviteError}</P2>
      </Notification>
    </>
  );
};

export default InviteAdminDialog;
